package com.example.hukakmp.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TimePicker
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.material3.rememberTimePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableDoubleStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.example.hukakmp.data.PescadexCatalog
import com.example.hukakmp.getPlatform
import com.example.hukakmp.interactiveMapAvailable
import com.example.hukakmp.model.EspecieEntry
import com.example.hukakmp.model.FotoSeleccionada
import com.example.hukakmp.model.ModalidadPesca
import com.example.hukakmp.model.NuevoParteInput
import com.example.hukakmp.util.formatearFechaDMY
import com.example.hukakmp.util.horaAMinutos
import com.example.hukakmp.viewmodel.ParteWizardViewModel
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.math.abs
import kotlin.math.round

private const val DEFAULT_MAP_LATITUDE = -43.30016
private const val DEFAULT_MAP_LONGITUDE = -65.10228

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun PasoFechaHora(
    input: NuevoParteInput,
    onActualizar: (transform: (NuevoParteInput) -> NuevoParteInput) -> Unit,
) {
    var mostrarFecha by remember { mutableStateOf(false) }
    var editarHora by remember { mutableStateOf<String?>(null) }

    StepQuestion("¿Cuándo pescaste?")
    PickerButton(
        label = "Fecha",
        value = input.fecha.ifBlank { "Seleccionar fecha" },
        onClick = { mostrarFecha = true },
        modifier = Modifier.padding(top = 12.dp),
    )

    Row(
        modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        PickerButton(
            label = "Inicio",
            value = input.horaInicio.ifBlank { "Elegir hora" },
            onClick = { editarHora = "inicio" },
            modifier = Modifier.weight(1f),
        )
        PickerButton(
            label = "Fin",
            value = input.horaFin.ifBlank { "Elegir hora" },
            onClick = { editarHora = "fin" },
            modifier = Modifier.weight(1f),
        )
    }

    Text(
        "Huka no permite registrar fechas futuras. Si la jornada es de hoy, tampoco permite horarios posteriores a la hora actual.",
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = Modifier.padding(top = 12.dp),
    )

    if (mostrarFecha) {
        val dateState = rememberDatePickerState(
            initialSelectedDateMillis = kotlin.time.Clock.System.now().toEpochMilliseconds(),
        )
        DatePickerDialog(
            onDismissRequest = { mostrarFecha = false },
            confirmButton = {
                TextButton(onClick = {
                    dateState.selectedDateMillis?.let { millis ->
                        val fecha = kotlin.time.Instant.fromEpochMilliseconds(millis)
                            .toLocalDateTime(TimeZone.UTC).date
                        onActualizar { it.copy(fecha = formatearFechaDMY(fecha)) }
                    }
                    mostrarFecha = false
                }) { Text("Aceptar") }
            },
            dismissButton = {
                TextButton(onClick = { mostrarFecha = false }) { Text("Cancelar") }
            },
        ) {
            DatePicker(state = dateState)
        }
    }

    editarHora?.let { destino ->
        val valorActual = if (destino == "inicio") input.horaInicio else input.horaFin
        SelectorHoraDialog(
            valorActual = valorActual,
            onDismiss = { editarHora = null },
            onConfirm = { hora ->
                onActualizar { actual ->
                    if (destino == "inicio") actual.copy(horaInicio = hora)
                    else actual.copy(horaFin = hora)
                }
                editarHora = null
            },
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun SelectorHoraDialog(
    valorActual: String,
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit,
) {
    val minutos = horaAMinutos(valorActual)
    val ahora = kotlin.time.Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).time
    val state = rememberTimePickerState(
        initialHour = minutos?.div(60) ?: ahora.hour,
        initialMinute = minutos?.rem(60) ?: ahora.minute,
        is24Hour = true,
    )
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Seleccionar horario") },
        text = { TimePicker(state = state) },
        confirmButton = {
            TextButton(onClick = {
                val hora = "${state.hour.toString().padStart(2, '0')}:${state.minute.toString().padStart(2, '0')}"
                onConfirm(hora)
            }) { Text("Aceptar") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancelar") } },
    )
}

@Composable
private fun PickerButton(
    label: String,
    value: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    OutlinedButton(
        onClick = onClick,
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Text(
                label,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(value, style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.onSurface)
        }
    }
}

@Composable
internal fun PasoModalidad(
    input: NuevoParteInput,
    onActualizar: (transform: (NuevoParteInput) -> NuevoParteInput) -> Unit,
) {
    StepQuestion("¿Cómo pescaste?")
    FlowRow(
        modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        ModalidadPesca.entries.forEach { modalidad ->
            val texto = if (modalidad == ModalidadPesca.OTRA) {
                "Otra"
            } else {
                modalidad.displayName.replaceFirstChar { it.uppercase() }
            }
            FilterChip(
                selected = input.modalidad == modalidad,
                onClick = {
                    onActualizar {
                        it.copy(
                            modalidad = modalidad,
                            modalidadOtra = if (modalidad == ModalidadPesca.OTRA) it.modalidadOtra else "",
                            numeroCanas = if (modalidad.usaCanas) it.numeroCanas else null,
                        )
                    }
                },
                label = {
                    Text(
                        if (getPlatform().name == "Web") texto
                        else "${modalidad.emoji} $texto"
                    )
                },
            )
        }
    }

    if (input.modalidad == ModalidadPesca.OTRA) {
        OutlinedTextField(
            value = input.modalidadOtra,
            onValueChange = { valor -> onActualizar { it.copy(modalidadOtra = valor) } },
            label = { Text("¿Cuál modalidad?") },
            singleLine = true,
            shape = RoundedCornerShape(14.dp),
            modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
        )
    }

    if (input.modalidad?.usaCanas == true) {
        OutlinedTextField(
            value = input.numeroCanas?.toString().orEmpty(),
            onValueChange = { valor ->
                onActualizar { it.copy(numeroCanas = valor.filter { c -> c.isDigit() }.toIntOrNull()) }
            },
            label = { Text("Número de cañas *") },
            singleLine = true,
            shape = RoundedCornerShape(14.dp),
            modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
        )
    }
}

@Composable
internal fun PasoUbicacion(
    input: NuevoParteInput,
    onActualizar: (transform: (NuevoParteInput) -> NuevoParteInput) -> Unit,
) {
    val latitudGuardada = input.latitud.trim().replace(',', '.').toDoubleOrNull()
    val longitudGuardada = input.longitud.trim().replace(',', '.').toDoubleOrNull()
    var mapLatitude by remember(latitudGuardada, longitudGuardada) {
        mutableDoubleStateOf(latitudGuardada ?: DEFAULT_MAP_LATITUDE)
    }
    var mapLongitude by remember(latitudGuardada, longitudGuardada) {
        mutableDoubleStateOf(longitudGuardada ?: DEFAULT_MAP_LONGITUDE)
    }

    val puntoConfirmado = latitudGuardada != null && longitudGuardada != null
    val mapaMovido = !puntoConfirmado ||
        abs(mapLatitude - latitudGuardada!!) > 0.00001 ||
        abs(mapLongitude - longitudGuardada!!) > 0.00001

    StepQuestion("¿Dónde pescaste?")
    Text(
        if (interactiveMapAvailable) {
            "Mové el mapa hasta que el pin quede exactamente sobre el lugar de pesca y después confirmá el punto."
        } else {
            "Usá tu ubicación actual o ingresá las coordenadas del lugar de pesca y después confirmá el punto."
        },
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = Modifier.padding(top = 4.dp),
    )

    HukaLocationMap(
        latitude = mapLatitude,
        longitude = mapLongitude,
        onCenterChanged = { lat, lon ->
            mapLatitude = lat.coerceIn(-90.0, 90.0)
            mapLongitude = lon.coerceIn(-180.0, 180.0)
        },
        modifier = Modifier
            .fillMaxWidth()
            .height(if (getPlatform().name == "Web") 560.dp else 310.dp)
            .padding(top = 14.dp),
    )

    Surface(
        modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
        shape = RoundedCornerShape(14.dp),
        color = if (puntoConfirmado && !mapaMovido) {
            MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.55f)
        } else {
            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.55f)
        },
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Text(
                if (puntoConfirmado && !mapaMovido) {
                    "✓ Punto confirmado"
                } else if (interactiveMapAvailable) {
                    "Punto bajo el pin"
                } else {
                    "Punto seleccionado"
                },
                fontWeight = FontWeight.Bold,
                style = MaterialTheme.typography.bodyMedium,
            )
            Text(
                "${formatCoordinate(mapLatitude)}, ${formatCoordinate(mapLongitude)}",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(top = 2.dp),
            )
        }
    }

    Button(
        onClick = {
            onActualizar {
                it.copy(
                    latitud = formatCoordinate(mapLatitude),
                    longitud = formatCoordinate(mapLongitude),
                )
            }
        },
        modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
        shape = RoundedCornerShape(14.dp),
    ) {
        Text(if (puntoConfirmado && mapaMovido) "Actualizar punto" else "Confirmar este punto")
    }

    if (puntoConfirmado && mapaMovido) {
        Text(
            if (interactiveMapAvailable) {
                "Moviste el mapa. Confirmá nuevamente para guardar la nueva ubicación."
            } else {
                "Cambiaste la ubicación. Confirmala nuevamente para guardar el nuevo punto."
            },
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.primary,
            modifier = Modifier.padding(top = 7.dp),
        )
    }

    OutlinedTextField(
        value = input.nombreLugar,
        onValueChange = { valor -> onActualizar { it.copy(nombreLugar = valor) } },
        label = { Text("Nombre del lugar (opcional)") },
        singleLine = true,
        shape = RoundedCornerShape(14.dp),
        modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
    )
    OutlinedTextField(
        value = input.zona,
        onValueChange = { valor -> onActualizar { it.copy(zona = valor) } },
        label = { Text("Provincia / zona (opcional)") },
        singleLine = true,
        shape = RoundedCornerShape(14.dp),
        modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
    )
}

private fun formatCoordinate(value: Double): String {
    val rounded = round(value * 1_000_000.0) / 1_000_000.0
    return rounded.toString()
}

@Composable
internal fun PasoEspecies(
    especies: List<EspecieEntry>,
    onAgregar: () -> Unit,
    onActualizar: (Int, EspecieEntry) -> Unit,
    onQuitar: (Int) -> Unit,
) {
    val webSpeciesNames = remember {
        if (getPlatform().name == "Web") {
            PescadexCatalog.species
                .map { it.nombreComun }
                .distinct()
                .sorted()
        } else {
            emptyList()
        }
    }

    StepQuestion("¿Qué pescaste?")
    if (webSpeciesNames.isNotEmpty()) {
        Text(
            "${webSpeciesNames.size} especies del catálogo de peces argentinos de Huka. Para registrar una captura, la especie debe estar validada en este catálogo.",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(top = 4.dp),
        )
    }
    if (especies.isEmpty()) {
        Surface(
            modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
            shape = RoundedCornerShape(14.dp),
            color = MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.5f),
        ) {
            Text(
                "Sin capturas también es un dato válido. Podés continuar o agregar una especie.",
                modifier = Modifier.padding(14.dp),
                style = MaterialTheme.typography.bodySmall,
            )
        }
    }

    Column(modifier = Modifier.fillMaxWidth().padding(top = 12.dp)) {
        especies.forEachIndexed { index, especie ->
            val speciesQuery = especie.especie.trim()
            val catalogValidationEnabled = webSpeciesNames.isNotEmpty()
            val exactSpeciesSelected =
                catalogValidationEnabled && PescadexCatalog.isKnownSpecies(speciesQuery)
            val canonicalSpeciesName =
                if (exactSpeciesSelected) PescadexCatalog.canonicalName(speciesQuery) else null
            val normalizedQuery = PescadexCatalog.normalizeSpeciesId(speciesQuery)
            val speciesSuggestions =
                if (catalogValidationEnabled && speciesQuery.isNotBlank() && !exactSpeciesSelected) {
                    webSpeciesNames
                        .filter { speciesName ->
                            PescadexCatalog.normalizeSpeciesId(speciesName).contains(normalizedQuery)
                        }
                        .take(6)
                } else {
                    emptyList()
                }

            Card(
                modifier = Modifier.fillMaxWidth().padding(bottom = 10.dp),
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.45f),
                ),
            ) {
                Column(modifier = Modifier.padding(12.dp)) {
                    Text(
                        "CAPTURA ${index + 1}",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.secondary,
                        fontWeight = FontWeight.Bold,
                    )
                    OutlinedTextField(
                        value = especie.especie,
                        onValueChange = { valor -> onActualizar(index, especie.copy(especie = valor)) },
                        label = { Text("Especie *") },
                        placeholder = {
                            if (catalogValidationEnabled) Text("Ej: Pejerrey")
                        },
                        isError = catalogValidationEnabled &&
                            speciesQuery.isNotBlank() &&
                            !exactSpeciesSelected,
                        supportingText = {
                            if (catalogValidationEnabled && speciesQuery.isNotBlank()) {
                                if (exactSpeciesSelected) {
                                    Text("✓ Validada en peces argentinos: ${canonicalSpeciesName ?: speciesQuery}")
                                } else {
                                    Text("No reconocida. Elegí una especie del catálogo de Huka.")
                                }
                            }
                        },
                        singleLine = true,
                        shape = RoundedCornerShape(14.dp),
                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                    )

                    if (speciesSuggestions.isNotEmpty()) {
                        Surface(
                            modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                            shape = RoundedCornerShape(12.dp),
                            color = MaterialTheme.colorScheme.surface,
                            tonalElevation = 2.dp,
                        ) {
                            Column {
                                speciesSuggestions.forEach { speciesName ->
                                    Text(
                                        speciesName,
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clickable {
                                                onActualizar(
                                                    index,
                                                    especie.copy(especie = speciesName),
                                                )
                                            }
                                            .padding(horizontal = 12.dp, vertical = 9.dp),
                                        style = MaterialTheme.typography.bodyMedium,
                                    )
                                }
                            }
                        }
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        OutlinedTextField(
                            value = especie.cantidad.toString(),
                            onValueChange = { valor ->
                                onActualizar(
                                    index,
                                    especie.copy(cantidad = valor.filter { c -> c.isDigit() }.toIntOrNull() ?: 0),
                                )
                            },
                            label = { Text("Cantidad *") },
                            singleLine = true,
                            shape = RoundedCornerShape(14.dp),
                            modifier = Modifier.weight(1f),
                        )
                        OutlinedTextField(
                            value = especie.devueltos.toString(),
                            onValueChange = { valor ->
                                onActualizar(
                                    index,
                                    especie.copy(devueltos = valor.filter { c -> c.isDigit() }.toIntOrNull() ?: 0),
                                )
                            },
                            label = { Text("Devueltos") },
                            singleLine = true,
                            shape = RoundedCornerShape(14.dp),
                            modifier = Modifier.weight(1f),
                        )
                    }
                    TextButton(onClick = { onQuitar(index) }) { Text("Quitar especie") }
                }
            }
        }
    }

    OutlinedButton(onClick = onAgregar, modifier = Modifier.fillMaxWidth()) {
        Text("+ Agregar especie")
    }
}

@Composable
internal fun PasoFoto(
    fotos: List<FotoSeleccionada>,
    cameraAvailable: Boolean,
    pickerError: String?,
    pickerLoading: Boolean,
    onCamara: () -> Unit,
    onGaleria: () -> Unit,
    onQuitar: (Int) -> Unit,
) {
    StepQuestion("Agregá evidencia fotográfica")
    Text(
        "Necesitás al menos una foto. Podés adjuntar hasta ${ParteWizardViewModel.MAX_FOTOS}, de un máximo de 10 MB cada una.",
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = Modifier.padding(top = 4.dp),
    )

    Row(
        modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        if (cameraAvailable) {
            Button(
                onClick = onCamara,
                enabled = !pickerLoading && fotos.size < ParteWizardViewModel.MAX_FOTOS,
                modifier = Modifier.weight(1f),
                shape = RoundedCornerShape(14.dp),
            ) { Text(if (getPlatform().name == "Web") "Cámara" else "📷 Cámara") }
        }
        OutlinedButton(
            onClick = onGaleria,
            enabled = !pickerLoading && fotos.size < ParteWizardViewModel.MAX_FOTOS,
            modifier = Modifier.weight(1f),
            shape = RoundedCornerShape(14.dp),
        ) {
            Text(
                if (getPlatform().name == "Web") {
                    if (cameraAvailable) "Galería" else "Elegir foto"
                } else {
                    if (cameraAvailable) "🖼️ Galería" else "🖼️ Elegir foto"
                }
            )
        }
    }

    if (pickerLoading) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
            Text("  Abriendo selector…", style = MaterialTheme.typography.bodySmall)
        }
    }

    pickerError?.let { WizardErrorCard(it) }

    if (fotos.isEmpty()) {
        Surface(
            modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
            shape = RoundedCornerShape(16.dp),
            color = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.45f),
        ) {
            Text("Todavía no agregaste ninguna fotografía.", modifier = Modifier.padding(16.dp))
        }
    } else {
        Column(modifier = Modifier.fillMaxWidth().padding(top = 12.dp)) {
            fotos.forEachIndexed { index, foto ->
                Card(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                    shape = RoundedCornerShape(14.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.45f),
                    ),
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        if (getPlatform().name != "Web") {
                            Text("📷", modifier = Modifier.padding(end = 10.dp))
                        }
                        Column(modifier = Modifier.weight(1f)) {
                            Text(foto.nombre, fontWeight = FontWeight.Bold, maxLines = 1)
                            Text(
                                "${foto.bytes.size / 1024} KB · ${foto.contentType}",
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                        TextButton(onClick = { onQuitar(index) }) { Text("Quitar") }
                    }
                }
            }
        }
    }
}

@Composable
internal fun PasoNotas(
    input: NuevoParteInput,
    onActualizar: (transform: (NuevoParteInput) -> NuevoParteInput) -> Unit,
) {
    StepQuestion("¿Querés agregar algo más?")
    OutlinedTextField(
        value = input.observaciones,
        onValueChange = { valor -> onActualizar { it.copy(observaciones = valor) } },
        label = { Text("Observaciones (opcional)") },
        minLines = 5,
        shape = RoundedCornerShape(14.dp),
        modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
    )
}

@Composable
private fun StepQuestion(text: String) {
    Text(text, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
}

@Composable
internal fun WizardErrorCard(message: String) {
    Surface(
        modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
        shape = RoundedCornerShape(14.dp),
        color = MaterialTheme.colorScheme.errorContainer,
    ) {
        Text(
            message,
            color = MaterialTheme.colorScheme.onErrorContainer,
            modifier = Modifier.padding(14.dp),
        )
    }
}
